import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { Appointment, Chair, NoShowRisk } from "@dentacore/shared-types";

import { changeAppointmentStatus } from "../api/appointments";
import { cancelLater } from "../api/plans";
import { ShiftDialog } from "../calendar/shift-dialog";
import { nextStatuses, statusAction, statusLabel } from "../calendar/status-graph";
import { beforeArrival, visitStep } from "../calendar/ring-steps";
import { announceRing } from "../shell/quick-ring";
import { RING_ICONS } from "../shell/ring/ring-icons";
import { useRingProvider } from "../shell/ring/ring-registry";
import { clockOf, isRemaining, watchToday } from "./home-data";
import styles from "./home.module.css";
import { Pager, fitArea, useRowPages } from "../ui/paging";

type Watch = ReturnType<typeof watchToday>[number];

/**
 * What is still to be done today, with the status control the mockup puts
 * inline so the front desk can advance a patient without leaving the screen.
 *
 * **The moves offered are the legal ones**, read from the same browser-side
 * mirror of the status graph the calendar's panel uses — imported rather than
 * copied, because a second copy of that table is a second thing to drift from
 * the server. The server still decides: an illegal move comes back 409 with a
 * sentence naming what *is* allowed, and that sentence is what gets shown.
 *
 * **Settled bookings drop out of the list** rather than greying, which is what
 * makes it a list of work left. The count in the header says how many.
 *
 * **It scrolls inside its own box** (decision CS2), so a busy day never makes
 * Home taller than the window — the summary line and the next patient stay put
 * however many are booked.
 *
 * **Watch today lives on the rows** (CS3): a critical flag or a no-show risk is
 * a chip on that patient's row, detail included. That panel only ever covered
 * patients still to be seen, which is exactly this list, so nothing drops out.
 * The waiting list that used to sit under this is in *Needs you* now.
 */
export function RemainingToday({
  appointments,
  chairs,
  risks,
  timezone,
  onChanged,
  onError,
  tag,
}: {
  /** The location a row belongs to, when several are shown. */
  tag?: (appointment: Appointment) => React.ReactNode;
  appointments: readonly Appointment[] | null;
  chairs: readonly Chair[];
  /** Today's patients who have been missing appointments (STORY-088). */
  risks: readonly NoShowRisk[];
  timezone: string;
  onChanged: () => void;
  onError: (message: string) => void;
}): React.JSX.Element {
  const [busyId, setBusyId] = useState<string | null>(null);
  /**
   * A chained visit being cancelled or marked a no-show, while the person
   * decides about the rest of its plan (CH11, CH13) — the same question the
   * calendar's panel asks, so Home cannot quietly behave differently.
   */
  const [cancelAsk, setCancelAsk] = useState<{ appointment: Appointment; to: string } | null>(null);

  const remaining = (appointments ?? [])
    .filter(isRemaining)
    .slice()
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt));

  const chairName = (chairId: string): string =>
    chairs.find((chair) => chair.id === chairId)?.name ?? "—";

  /** Each remaining booking's watch items — critical flags and no-show risk. */
  const watchesByBooking = new Map<string, Watch[]>();
  for (const watch of watchToday(appointments ?? [], risks)) {
    const list = watchesByBooking.get(watch.appointment.id) ?? [];
    list.push(watch);
    watchesByBooking.set(watch.appointment.id, list);
  }

  const move = (
    appointment: Appointment,
    to: string,
    /** Set once the question has been answered: whether to cancel the rest. */
    answered?: { alsoCancelLater: boolean },
  ): Promise<boolean> => {
    const chain = appointment.chain ?? null;
    const alsoCancelLater = answered?.alsoCancelLater ?? false;
    if (
      answered === undefined &&
      (to === "CANCELLED" || to === "NO_SHOW") &&
      chain !== null &&
      chain.laterBooked > 0
    ) {
      setCancelAsk({ appointment, to });
      return Promise.resolve(false);
    }

    setBusyId(appointment.id);

    return changeAppointmentStatus(appointment.id, { status: to })
      .then(async () => {
        if (alsoCancelLater && chain !== null) {
          const result = await cancelLater(chain.planId, appointment.id);
          if (result.refused.length > 0) {
            onError(
              `Done, but ${String(result.refused.length)} later ${
                result.refused.length === 1 ? "visit" : "visits"
              } could not be cancelled: ${result.refused.map((row) => row.message).join(" ")}`,
            );
          }
        }
        onChanged();
        return true;
      })
      .catch((cause: unknown) => {
        onError(
          cause instanceof Error && cause.message !== ""
            ? cause.message
            : "That status change did not go through.",
        );
        return false;
      })
      .finally(() => setBusyId(null));
  };

  const navigate = useNavigate();

  /**
   * **A patient still to be seen, on the quick ring** (STORY-221, `home.visit`).
   * Today's row and *Up next* both carry `data-ring="home.visit"` and the
   * booking's id.
   *
   * Every action is a button that already exists: the steps are this row's
   * own status control (`move`, so a chained visit still asks about the rest);
   * Complete visit and Reschedule live on the calendar's panel, so they open
   * it there and press that button; Book next is the patient file's link into
   * the booking form. *Take payment* is left out: the panel only takes one for
   * a completed visit, and a completed visit is no longer on this list.
   */
  useRingProvider("home.visit", (element) => {
    const appointment = (appointments ?? []).find((row) => row.id === element.dataset.id);
    const who = appointment?.patient ?? null;
    if (appointment === undefined || who === null) return null;
    const name = `${who.firstName} ${who.lastName}`.trim();
    const first = who.firstName === "" ? name : who.firstName;
    const step = visitStep(appointment.status);
    const [firstMove, secondMove] = step?.to ?? [];
    const runStep = (): void => {
      if (firstMove === undefined) return;
      // Completing charts and bills, so it goes through the calendar's own dialog.
      if (firstMove === "COMPLETED") {
        navigate(`/calendar?appointment=${appointment.id}&ring=step`);
        return;
      }
      const said =
        firstMove === "IN_CHAIR" ? `${first} is in the chair.` : `${first} is checked in.`;
      void move(appointment, firstMove).then(async (saved) => {
        if (saved && secondMove !== undefined) saved = await move(appointment, secondMove);
        if (saved) announceRing(said);
      });
    };
    return {
      ring: "home.visit",
      title: name,
      meta: `${clockOf(appointment.startsAt, timezone)} · ${statusLabel(appointment.status)}`,
      patientId: who.id,
      subject: { type: "booking", id: appointment.id },
      actions: [
        step !== null && {
          key: "step",
          label: step.label,
          icon: step.icon,
          needs: ["appointments.status"],
          hint: firstMove === "COMPLETED" ? "Opens Complete visit on the calendar" : step.hint,
          run: runStep,
        },
        appointment.status !== "COMPLETED" &&
          appointment.status !== "CANCELLED" && {
            key: "move",
            label: "Reschedule",
            icon: RING_ICONS.move,
            needs: ["appointments.move"],
            hint: "Pick a new time, on the calendar",
            run: () => navigate(`/calendar?appointment=${appointment.id}&ring=move`),
          },
        beforeArrival(appointment.status) && {
          key: "noshow",
          label: statusAction("NO_SHOW", appointment.status),
          icon: RING_ICONS.noshow,
          needs: ["appointments.status"],
          danger: true,
          hint: "They did not come; frees the chair",
          run: () => {
            void move(appointment, "NO_SHOW").then((saved) => {
              if (saved) announceRing(`${first} marked no-show.`);
            });
          },
        },
        {
          key: "next",
          label: "Book next",
          icon: RING_ICONS.book,
          needs: ["appointments.book"],
          hint: "Books their next visit",
          run: () => navigate(`/calendar?patient=${who.id}`),
        },
        {
          key: "file",
          label: "Open file",
          icon: RING_ICONS.file,
          needs: ["patients.view"],
          hint: "Opens their file",
          run: () => navigate(`/patients/${who.id}`),
        },
      ],
    };
  });

  // A busy day is more rows than the column holds: whole rows, paged (no scroll).
  const paging = useRowPages({ count: remaining.length, estimate: 52 });

  return (
    <section className={`${styles.panel} ${styles.todayPanel}`} data-testid="today-panel">
      {cancelAsk !== null && (
        <ShiftDialog
          kind="cancel"
          later={cancelAsk.appointment.chain?.laterBooked ?? 0}
          onAnswer={(answer) => {
            const asked = cancelAsk;
            setCancelAsk(null);
            if (answer === null) return;
            void move(asked.appointment, asked.to, { alsoCancelLater: answer });
          }}
        />
      )}

      <div className={styles.panelHead}>
        <h2 className={styles.panelTitle}>Today</h2>
        <span className={styles.panelNote} data-testid="remaining-summary">
          {appointments === null
            ? "Reading…"
            : `${remaining.length} left · ${chairsInUse(remaining)} ${
                chairsInUse(remaining) === 1 ? "chair" : "chairs"
              }`}
          {/*
            *Watch today* said so in words when nobody still to be seen had a
            critical flag or a no-show risk — an empty panel reads as one that
            failed to load. With the flags on the rows, that sentence is here.
          */}
          {appointments !== null && remaining.length > 0 && watchesByBooking.size === 0 && (
            <span data-testid="watch-empty"> · nothing flagged</span>
          )}
        </span>
        <Link to="/calendar" className={styles.panelLink}>
          Open calendar
        </Link>
      </div>

      {appointments === null ? (
        <p className={styles.quiet}>Reading&hellip;</p>
      ) : remaining.length === 0 ? (
        <p className={styles.blank} data-testid="nothing-remaining">
          Nothing left today. Every booking has been seen, cancelled or marked a no-show.
        </p>
      ) : (
        <>
          <div className={`${fitArea} ${styles.fillArea}`} ref={paging.areaRef}>
            <ul className={styles.rows}>
              {remaining.slice(paging.start, paging.end).map((appointment, index) => (
                <li
                  key={appointment.id}
                  ref={index === 0 ? paging.rowRef : undefined}
                  className={styles.row}
                  data-testid="remaining-row"
                  // A patient still to be seen, to the quick ring (STORY-221).
                  data-ring="home.visit"
                  data-id={appointment.id}
                >
                  <span className={styles.time}>{clockOf(appointment.startsAt, timezone)}</span>

                  <span className={styles.rowMain}>
                    <span className={styles.rowLine}>
                      <Link
                        to={`/patients/${appointment.patient?.id ?? ""}`}
                        className={styles.patient}
                        data-testid="remaining-patient"
                      >
                        {appointment.patient?.firstName} {appointment.patient?.lastName}
                      </Link>
                      {tag?.(appointment)}
                      {(watchesByBooking.get(appointment.id) ?? []).map((watch) => (
                        <WatchChip key={`${watch.kind}-${watch.label}`} watch={watch} />
                      ))}
                    </span>
                    <span className={styles.rowSub}>
                      {appointment.procedureSummary ?? "No procedure noted"} &middot;{" "}
                      {chairName(appointment.chairId)}
                    </span>
                  </span>

                  {/*
                A select of the legal next moves, not of all seven. "Move to…"
                is the resting value rather than the current status: a control
                whose value is the status invites reading it as a label, and a
                reader who then picks the value already shown gets an error
                for asking for nothing.
              */}
                  <select
                    className={styles.status}
                    value=""
                    data-status={appointment.status}
                    disabled={busyId === appointment.id}
                    aria-label={`Status of ${appointment.patient?.firstName ?? "this booking"}'s appointment, currently ${statusLabel(appointment.status)}`}
                    onChange={(event) => {
                      if (event.target.value !== "") void move(appointment, event.target.value);
                    }}
                    data-testid="remaining-status"
                  >
                    <option value="">{statusLabel(appointment.status)}</option>
                    {nextStatuses(appointment.status).map((to) => (
                      <option key={to} value={to}>
                        {statusAction(to, appointment.status)}
                      </option>
                    ))}
                  </select>
                </li>
              ))}
            </ul>
          </div>
          <Pager
            page={paging.page}
            count={paging.pages}
            onPage={paging.setPage}
            from={paging.start + 1}
            to={paging.end}
            total={remaining.length}
            noun="left today"
            testId="remaining-pager"
          />
        </>
      )}
    </section>
  );
}

/**
 * One *Watch today* item, as a chip on the patient's row (CS3).
 *
 * **A word as well as a colour**: the kind ("Allergy", "No-show risk") is
 * spelled out, so the chip never relies on red alone — the same rule the old
 * panel and the tooth chart follow. The detail rides along in the chip, and
 * in its title for when a narrow row truncates it.
 */
function WatchChip({ watch }: { watch: Watch }): React.JSX.Element {
  const risk = watch.kind === "No-show risk";
  return (
    <span
      className={risk ? styles.chipWarm : styles.chipCritical}
      title={watch.detail === null ? undefined : `${watch.label} — ${watch.detail}`}
      data-testid="watch-row"
    >
      {watch.kind} · {watch.label}
      {watch.detail !== null && ` — ${watch.detail}`}
    </span>
  );
}

/** How many distinct chairs the remaining work still occupies. */
function chairsInUse(remaining: readonly Appointment[]): number {
  return new Set(remaining.map((appointment) => appointment.chairId)).size;
}
